/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import axios, { CancelToken } from "axios";
import { OutputSchemaType } from "../../store/dataset-ingestion/dataset-ingestion.interfaces";
import {
    CategoryTypes,
    Connection,
    ConnectionConfig,
    ConnectionProperties,
    ConnectionTypes,
    DataSourceCategory,
    DataSourceTypesResponseType,
    EditableConnection,
    ExploreDataSourcesArgsType,
    ExploreDataSourcesResponseType,
    InferConfigArgsType,
    InferConfigResponseType,
    InferSchemaArgsType,
    InferSchemaFromSampleArgsType,
    InferSchemaFromSampleResponseType,
    InferSchemaResponseType,
    IngestionProperties,
    PreviewIngestionArgsType,
    PreviewIngestionResponseType,
    RunIngestionArgsType,
    RunIngestionResponseType,
    TenantsResponseType,
    TestConnectionArgsType,
    TestConnectionResponseType,
    ValidateConfigArgsType,
    ValidateResponseType,
    ValidateSchemaArgsType,
} from "../dto/datasources.interfaces";

const BASE_URL = "/api/v1/data-manager/datasources";

export const createDataSource = async (
    data: EditableConnection
): Promise<Connection> => {
    const response = await axios.post(BASE_URL, data);

    return response.data;
};

export const deleteDataSource = async (
    dataSourceSrn: Connection["srn"]
): Promise<string> => {
    const response = await axios.delete(`${BASE_URL}/${dataSourceSrn}`);

    return response.data;
};

export const exploreDataSources = async (
    data: ExploreDataSourcesArgsType,
    cancelToken?: CancelToken
): Promise<ExploreDataSourcesResponseType> => {
    const response = await axios.post(`${BASE_URL}/explore-datasource`, data, {
        cancelToken,
    });

    return response.data;
};

export const getConnectionProperties = async (): Promise<
    ConnectionProperties[]
> => {
    const response = await axios.get(`${BASE_URL}/connection-properties`);

    return response.data;
};

export const getConnectionPropertiesByType = async (
    type: ConnectionTypes
): Promise<ConnectionProperties[]> => {
    const response = await axios.get(
        `${BASE_URL}/connection-properties/${type}`
    );

    return response.data;
};

export const getDataSource = async (
    srn: Connection["srn"]
): Promise<Connection> => {
    const response = await axios.get(`${BASE_URL}/${srn}`);

    return response.data;
};

export const getDataSourceCategories = async (): Promise<
    DataSourceCategory[]
> => {
    const response = await axios.get(`${BASE_URL}/categories`);

    return response.data;
};

// Successful response is either `[]` or `[Datasource, Datasource, ...]
export const getDataSources = async (): Promise<Connection[]> => {
    const response = await axios.get<Connection[]>(`${BASE_URL}`);

    // Hide AWS MSK data sources until UI is implemented
    const filteredData = response.data.filter(
        (data) => data.type !== ConnectionTypes.AWS_MSK
    );

    return filteredData;
};

export const getDataSourcesTypes =
    async (): Promise<DataSourceTypesResponseType> => {
        const response = await axios.get<DataSourceTypesResponseType>(
            `${BASE_URL}/types`
        );

        // Hide AWS MSK data source type until UI is implemented
        const filteredData = response.data.filter(
            (data) => data.type !== ConnectionTypes.AWS_MSK
        );

        return filteredData;
    };

export const getDataSourcesTypesByCategory = async (
    categoryType: CategoryTypes
): Promise<DataSourceTypesResponseType> => {
    const response = await axios.get(`${BASE_URL}/types/${categoryType}`);

    return response.data;
};

export const getIngestionProperties = async (
    type: ConnectionTypes | ""
): Promise<IngestionProperties[]> => {
    const response = await axios.get(
        `${BASE_URL}/ingestion-properties/${type}`
    );

    return response.data;
};

export const getIngestionPropertiesV2 = async (data: {
    dataSourceType: ConnectionTypes | "";
    ingestionConnection: ConnectionConfig;
}): Promise<IngestionProperties[]> => {
    const response = await axios.post(
        `${BASE_URL}/ingestion-properties-v2`,
        data
    );

    return response.data;
};

export const getTenants = async (
    srn: Connection["srn"]
): Promise<TenantsResponseType> => {
    const response = await axios.get(`${BASE_URL}/${srn}/tenants`);

    return response.data;
};

export const inferConfig = async (
    data: InferConfigArgsType
): Promise<InferConfigResponseType> => {
    const response = await axios.post<InferConfigResponseType>(
        `${BASE_URL}/infer-config-v2`,
        data
    );

    return response.data;
};

export const inferSchema = async (
    data: InferSchemaArgsType
): Promise<InferSchemaResponseType> => {
    const response = await axios.post(`${BASE_URL}/infer-schema-v2`, data);

    return response.data;
};

export const inferSchemaFromSample = async (
    data: InferSchemaFromSampleArgsType
): Promise<InferSchemaFromSampleResponseType> => {
    const response = await axios.post(
        `${BASE_URL}/infer-schema-from-sample`,
        data
    );

    return response.data;
};

export const previewIngestion = async (
    data: PreviewIngestionArgsType
): Promise<PreviewIngestionResponseType> => {
    const params: Record<string, string> = {};

    // This is mainly used for E2E tests
    if (data.outputSchema?.type) {
        params.schema =
            data.outputSchema?.type === OutputSchemaType.NONE
                ? "input"
                : "output";
    }

    const response = await axios.post(
        `${BASE_URL}/preview-ingestion-v2`,
        data,
        { params }
    );

    return response.data;
};

export const runIngestion = async (
    data: RunIngestionArgsType
): Promise<RunIngestionResponseType> => {
    const response = await axios.post(`${BASE_URL}/run-ingestion`, data);

    return response.data;
};

export const testConnection = async (
    data: TestConnectionArgsType
): Promise<TestConnectionResponseType> => {
    const response = await axios.post(`${BASE_URL}/test-datasource`, data);

    return response.data;
};

export const validateConfig = async (
    data: ValidateConfigArgsType
): Promise<ValidateResponseType> => {
    const response = await axios.post(`${BASE_URL}/validate-config`, data);

    return response.data;
};

export const validateSchema = async (
    data: ValidateSchemaArgsType
): Promise<ValidateResponseType> => {
    const response = await axios.post(`${BASE_URL}/validate-schema`, data);

    return response.data;
};
